<template>
    <div class="z-10">
        <div class="relative">

            <Button outlined class="w-20" @click="ehrFilterPopover.toggle($event)"
                :label="$t('messages.transactions.ehr')" :class="{ 'p-button-primary': isDirty }"
                :badge="activeFilterCount > 0 ? String(activeFilterCount) : null" badgeSeverity="primary"
                :disabled="disabled" />

            <div class="absolute left-0 top-full mt-1 z-50">
                <Popover ref="ehrFilterPopover" class="max-w-sm">
                    <div class="flex flex-col max-h-[70vh]">
                    <div class="ehr-filter-scroll flex flex-col gap-6 overflow-y-auto pr-2">
                    <!-- Section 1: Numeric Ranges -->
                    <div class="flex flex-col gap-4">
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-semibold tracking-tight text-gray-700 dark:text-gray-300">
                                {{ $t('messages.transactions.ehr_section.numeric') }}
                            </span>
                            <div class="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
                        </div>

                        <div class="flex flex-wrap gap-4 justify-center">
                            <!-- Year -->
                            <div class="flex flex-col group">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div
                                        class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate">
                                        {{ $t('messages.transactions.ehr_fields.ehitise_esmane_kasutus') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1.5 rounded-md shadow-sm flex gap-1">
                                    <InputNumber v-model="model.year.from"
                                        :placeholder="$t('messages.transactions.from')" size="small" :pt="fromToPT"
                                        :useGrouping="false" inputmode="numeric" />
                                    <InputNumber v-model="model.year.to" :placeholder="$t('messages.transactions.to')"
                                        size="small" :pt="fromToPT" :useGrouping="false" inputmode="numeric" />
                                </div>
                            </div>

                            <!-- Floors -->
                            <div class="flex flex-col group">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div
                                        class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate">
                                        {{ $t('messages.transactions.ehr_fields.ehitise_max_korruste_arv') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1.5 rounded-md shadow-sm flex gap-1">
                                    <InputNumber v-model="model.floors.from"
                                        :placeholder="$t('messages.transactions.from')" size="small" :pt="fromToPT"
                                        :useGrouping="false" inputmode="numeric" />
                                    <InputNumber v-model="model.floors.to" :placeholder="$t('messages.transactions.to')"
                                        size="small" :pt="fromToPT" :useGrouping="false" inputmode="numeric" />
                                </div>
                            </div>

                            <!-- Floor -->
                            <div class="flex flex-col group">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div
                                        class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate">
                                        {{ $t('messages.transactions.ehr_fields.hooneosa_sissepaasu_korrus') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1.5 rounded-md shadow-sm flex gap-1">
                                    <InputNumber v-model="model.floor.from"
                                        :placeholder="$t('messages.transactions.from')" size="small" :pt="fromToPT"
                                        :useGrouping="false" inputmode="numeric" />
                                    <InputNumber v-model="model.floor.to" :placeholder="$t('messages.transactions.to')"
                                        size="small" :pt="fromToPT" :useGrouping="false" inputmode="numeric" />
                                </div>
                            </div>

                            <!-- Rooms -->
                            <div class="flex flex-col group">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div
                                        class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate">
                                        {{ $t('messages.transactions.ehr_fields.hooneosa_tubade_arv') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1.5 rounded-md shadow-sm flex gap-1">
                                    <InputNumber v-model="model.rooms.from"
                                        :placeholder="$t('messages.transactions.from')" size="small" :pt="fromToPT"
                                        :useGrouping="false" inputmode="numeric" />
                                    <InputNumber v-model="model.rooms.to" :placeholder="$t('messages.transactions.to')"
                                        size="small" :pt="fromToPT" :useGrouping="false" inputmode="numeric" />
                                </div>
                            </div>

                            <!-- Part Area -->
                            <div class="flex flex-col group">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div
                                        class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate">
                                        {{ $t('messages.transactions.ehr_fields.hooneosa_pind') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1.5 rounded-md shadow-sm flex gap-1">
                                    <InputNumber v-model="model.partArea.from"
                                        :placeholder="$t('messages.transactions.from')" size="small" :pt="fromToPT"
                                        :useGrouping="false" inputmode="numeric" suffix=" m²" />
                                    <InputNumber v-model="model.partArea.to"
                                        :placeholder="$t('messages.transactions.to')" size="small" :pt="fromToPT"
                                        :useGrouping="false" inputmode="numeric" suffix=" m²" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Section 2: Categorical Filters -->
                    <div class="flex flex-col flex-wrap gap-4">
                        <div class="flex items-center gap-2">
                            <span class="text-sm font-semibold tracking-tight text-gray-700 dark:text-gray-300">
                                {{ $t('messages.transactions.ehr_section.categorical') }}
                            </span>
                            <div class="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
                        </div>

                        <div class="flex flex-wrap gap-4 justify-center">
                            <!-- Construction Material -->
                            <div class="flex flex-col group w-full">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate"
                                        :title="$t('messages.transactions.ehr_fields.ehitise_konstruktsioonide_materjal')">
                                        {{ $t('messages.transactions.ehr_fields.ehitise_konstruktsioonide_materjal') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1 rounded-md shadow-sm">
                                    <MultiSelect v-model="model.construction"
                                        :options="options['ehitise_konstruktsioonide_materjal'] || []"
                                        optionLabel="label" size="small" fluid :selectAllOption="true"
                                        filter
                                        :placeholder="$t('messages.transactions.choose')" :maxSelectedLabels="1"
                                        :selectedItemsLabel="`{0} ${$t('messages.transactions.chosen')}`" />
                                </div>
                            </div>

                            <!-- Wall Type -->
                            <div class="flex flex-col group w-full">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate"
                                        :title="$t('messages.transactions.ehr_fields.ehitise_valisseina_liik')">
                                        {{ $t('messages.transactions.ehr_fields.ehitise_valisseina_liik') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1 rounded-md shadow-sm">
                                    <MultiSelect v-model="model.wall"
                                        :options="options['ehitise_valisseina_liik'] || []" optionLabel="label"
                                        size="small" fluid :selectAllOption="true" filter
                                        :placeholder="$t('messages.transactions.choose')" :maxSelectedLabels="1"
                                        :selectedItemsLabel="`{0} ${$t('messages.transactions.chosen')}`" />
                                </div>
                            </div>

                            <!-- Heating Type -->
                            <div class="flex flex-col group w-full">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate"
                                        :title="$t('messages.transactions.ehr_fields.hooneosa_soojavarustuse_liik')">
                                        {{ $t('messages.transactions.ehr_fields.hooneosa_soojavarustuse_liik') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1 rounded-md shadow-sm">
                                    <MultiSelect v-model="model.partHeating"
                                        :options="options['hooneosa_soojavarustuse_liik'] || []" optionLabel="label"
                                        size="small" fluid :selectAllOption="true" filter
                                        :placeholder="$t('messages.transactions.choose')" :maxSelectedLabels="1"
                                        :selectedItemsLabel="`{0} ${$t('messages.transactions.chosen')}`" />
                                </div>
                            </div>

                            <!-- Building Purpose -->
                            <div class="flex flex-col group w-full">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate"
                                        :title="$t('messages.transactions.ehr_fields.ehr_purpose')">
                                        {{ $t('messages.transactions.ehr_fields.ehr_purpose') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1 rounded-md shadow-sm">
                                    <MultiSelect v-model="model.purpose" :options="options['ehr_purpose'] || []"
                                        optionLabel="label" size="small" fluid :selectAllOption="true" filter
                                        :placeholder="$t('messages.transactions.choose')" :maxSelectedLabels="1"
                                        :selectedItemsLabel="`{0} ${$t('messages.transactions.chosen')}`" />
                                </div>
                            </div>

                            <!-- Part Purpose -->
                            <div class="flex flex-col group w-full">
                                <span
                                    class="text-xs text-gray-600 dark:text-gray-400 -mb-1 flex font-semibold tracking-tight">
                                    <div class="py-1 px-2 bg-linear-to-b from-white/75 to-white dark:from-gray-800/75 dark:to-gray-800 rounded-md rounded-b-none w-full text-center truncate"
                                        :title="$t('messages.transactions.ehr_fields.ehr_part_purpose')">
                                        {{ $t('messages.transactions.ehr_fields.ehr_part_purpose') }}
                                    </div>
                                </span>
                                <div class="bg-white dark:bg-gray-800 p-1 rounded-md shadow-sm">
                                    <MultiSelect v-model="model.partPurpose" :options="options['ehr_purpose'] || []"
                                        optionLabel="label" size="small" fluid :selectAllOption="true" filter
                                        :placeholder="$t('messages.transactions.choose')" :maxSelectedLabels="1"
                                        :selectedItemsLabel="`{0} ${$t('messages.transactions.chosen')}`" />
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>

                    <!-- Reset Button -->
                    <div v-if="isDirty"
                        class="flex justify-end shrink-0 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <Button severity="secondary" size="small" @click.stop.prevent="reset">
                            <span class="pi pi-times mr-1"></span>
                            {{ $t('messages.transactions.reset') }}
                        </Button>
                    </div>
                    </div>
                </Popover>
            </div>
        </div>

    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { SEARCH_EHR_DEFAULTS } from '../../../constants'
import { deepCopy } from '../../../services/utilities'

const props = defineProps({
    disabled: {
        type: Boolean,
        default: true,
    },
    isKom: {
        type: Boolean,
        default: false,
    },
    isKasi: {
        type: Boolean,
        default: false,
    },
    texts: {
        type: Object,
        default: () => ({}),
    },
    options: {
        type: Object,
        default: () => ({}),
    },
})

const ehrFilterPopover = ref(null)

const fromToPT = ref({
    root: {
        class: 'w-full',
    },
    pcInputText: {
        root: {
            class: 'w-full min-w-18 text-center',
        },
    },
})

const model = defineModel({
    default: deepCopy(SEARCH_EHR_DEFAULTS),
})

const isDirty = computed(() => {
    return JSON.stringify(model.value) !== JSON.stringify(SEARCH_EHR_DEFAULTS)
})

const activeFilterCount = computed(() => {
    let count = 0

    // Numeric ranges
    if (model.value.year.from || model.value.year.to) count++
    if (model.value.floors.from || model.value.floors.to) count++
    if (model.value.floor.from || model.value.floor.to) count++
    if (model.value.rooms.from || model.value.rooms.to) count++
    if (model.value.partArea.from || model.value.partArea.to) count++

    // Categorical
    if (model.value.construction.length) count++
    if (model.value.wall.length) count++
    if (model.value.partHeating.length) count++
    if (model.value.purpose.length) count++
    if (model.value.partPurpose.length) count++

    return count
})

const reset = () => {
    model.value = deepCopy(SEARCH_EHR_DEFAULTS)
}
</script>

<style scoped>
  @reference '../../../../css/tailwind.css';

.ehr-filter-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgb(203 213 225) transparent;

    &::-webkit-scrollbar {
        width: 12px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
        @apply bg-gray-300 dark:bg-gray-600;
        border-radius: 10px;
        border: 2px solid transparent;
        background-clip: content-box;

        &:hover {
            @apply bg-gray-400 dark:bg-gray-500;
        }

        &:active {
            @apply bg-gray-500 dark:bg-gray-400;
        }
    }

    .dark & {
        scrollbar-color: rgb(75 85 99) transparent;
    }
}
</style>
